import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';

const auditAreas = [
  { icon: 'ri-money-dollar-circle-line', label: 'Billing & Coding', color: '#BE185D' },
  { icon: 'ri-shield-check-line', label: 'Compliance & HIPAA', color: '#7C3AED' },
  { icon: 'ri-settings-3-line', label: 'Operations & Workflow', color: '#1B4B7F' },
  { icon: 'ri-line-chart-line', label: 'Revenue Cycle (RCM)', color: '#0D9488' },
  { icon: 'ri-team-line', label: 'Staffing & HR', color: '#D97706' },
];

const auditTiers = [
  {
    id: 'mini',
    name: 'Mini Practice Audit',
    badge: 'QUICK WIN',
    badgeColor: '#0D9488',
    time: '3–5 business days',
    price: 'Complimentary',
    desc: 'A rapid 5-area health check that surfaces your top critical issues fast. Perfect as a starting point.',
    includes: [
      'Billing spot-check (25 claims)',
      'Compliance gap scan',
      'RCM KPI snapshot',
      'Staffing ratio review',
      'Written summary with top findings',
    ],
  },
  {
    id: 'full',
    name: 'Full Practice Audit',
    badge: 'COMPREHENSIVE',
    badgeColor: '#BE185D',
    time: '15–30 business days',
    price: 'Custom Quote',
    desc: 'The complete diagnostic — every system, every process, every dollar. Graded findings + 90-day action plan.',
    includes: [
      'Full billing & coding review (100+ claims)',
      'HIPAA risk assessment',
      'Complete RCM performance analysis',
      'Operational workflow mapping',
      'HR & staffing structure review',
      'Graded findings report',
      '90-day prioritized action plan',
      'Executive debrief call',
    ],
  },
];

const stats = [
  { value: '92%', label: 'Of audited practices uncover critical revenue gaps' },
  { value: '$47K', label: 'Average revenue recovered after audit' },
  { value: '30 days', label: 'Full audit turnaround time' },
  { value: '5 areas', label: 'Comprehensively reviewed every time' },
];

const faqs = [
  { q: 'How long does the audit take?', a: 'The Mini Audit is typically completed in 3–5 business days. The Full Practice Audit takes 15–30 business days depending on practice size and complexity.' },
  { q: 'Do I need to share patient data?', a: 'No. We work with de-identified billing data and operational documentation only. All information shared is protected under a mutual NDA signed before the engagement begins.' },
  { q: 'What happens after the audit?', a: 'You receive a detailed written report with graded findings and prioritized recommendations. We then schedule an executive debrief call to walk through the results and answer questions. There is no obligation to engage further.' },
  { q: 'Is the Mini Audit really free?', a: 'Yes — the Mini Practice Audit is complimentary. It\'s our way of demonstrating value before you commit to anything. No credit card, no contract.' },
  { q: 'Can you implement the recommendations?', a: 'Absolutely. Many clients choose to engage MedxMSO for ongoing management after seeing the audit results. We can handle implementation across any or all of our 7 service pillars.' },
];

export default function AuditPage() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [selectedTier, setSelectedTier] = useState<'mini' | 'full'>('mini');
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [charCount, setCharCount] = useState(0);
  const formRef = useRef<HTMLFormElement>(null);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (formStatus === 'submitting') return;
    setFormStatus('submitting');

    const form = e.currentTarget;
    const data = new URLSearchParams();
    const elements = form.elements as HTMLFormControlsCollection;

    Array.from(elements).forEach((el) => {
      const input = el as HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement;
      if (input.name) {
        if (input.type === 'checkbox') {
          if ((input as HTMLInputElement).checked) {
            data.append(input.name, input.value);
          }
        } else if (input.type === 'radio') {
          if ((input as HTMLInputElement).checked) {
            data.append(input.name, input.value);
          }
        } else {
          data.append(input.name, input.value);
        }
      }
    });

    try {
      const res = await fetch('https://readdy.ai/api/form/d79hg5j2hsar0k5m0ngg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: data.toString(),
      });
      if (res.ok) {
        setFormStatus('success');
        form.reset();
        setCharCount(0);
      } else {
        setFormStatus('error');
      }
    } catch {
      setFormStatus('error');
    }
  };

  return (
    <div className="min-h-screen bg-white font-sans">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-white shadow-md' : 'bg-gradient-to-b from-[#0A1628] to-[#BE185D]/80'}`}>
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            <Link to="/" className="flex items-center gap-3 cursor-pointer">
              <img
                src="https://static.readdy.ai/image/50f4002bde5ecf02750e3107d0496ef3/6f24c25eed393f63407e0e21645fee7d.png"
                alt="MedxMSO Logo"
                className="h-12 w-auto"
              />
              <span className={`text-xl font-black tracking-tight whitespace-nowrap ${scrolled ? 'text-[#1B4B7F]' : 'text-white'}`}>MedxMSO</span>
            </Link>
            <div className="hidden lg:flex items-center space-x-8">
              <Link to="/" className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${scrolled ? 'text-gray-700 hover:text-[#1B4B7F]' : 'text-white hover:text-gray-200'}`}>Home</Link>
              <Link to="/services" className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${scrolled ? 'text-gray-700 hover:text-[#1B4B7F]' : 'text-white hover:text-gray-200'}`}>Services</Link>
              <Link to="/pricing" className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${scrolled ? 'text-gray-700 hover:text-[#1B4B7F]' : 'text-white hover:text-gray-200'}`}>Models</Link>
              <Link to="/mso-pricing" className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${scrolled ? 'text-gray-700 hover:text-[#1B4B7F]' : 'text-white hover:text-gray-200'}`}>Vendor Pricing</Link>
              <Link to="/partnership" className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${scrolled ? 'text-[#059669] hover:text-[#047857]' : 'text-[#6EE7B7] hover:text-white'}`}>Partnership</Link>
              <Link to="/why-mso" className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${scrolled ? 'text-gray-700 hover:text-[#1B4B7F]' : 'text-white hover:text-gray-200'}`}>Why MSO?</Link>
              <Link to="/audit" className={`text-sm font-semibold transition-colors whitespace-nowrap cursor-pointer ${scrolled ? 'text-[#BE185D]' : 'text-white'}`}>Request Audit</Link>
            </div>
            <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="lg:hidden p-2 cursor-pointer">
              <i className={`${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl ${scrolled ? 'text-gray-700' : 'text-white'}`}></i>
            </button>
          </div>
        </div>
        {mobileMenuOpen && (
          <div className="lg:hidden bg-white border-t">
            <div className="px-6 py-4 space-y-3">
              <Link to="/" className="block text-sm font-medium text-gray-700 hover:text-[#1B4B7F] cursor-pointer">Home</Link>
              <Link to="/services" className="block text-sm font-medium text-gray-700 hover:text-[#1B4B7F] cursor-pointer">Services</Link>
              <Link to="/pricing" className="block text-sm font-medium text-gray-700 hover:text-[#1B4B7F] cursor-pointer">Models</Link>
              <Link to="/mso-pricing" className="block text-sm font-medium text-gray-700 hover:text-[#1B4B7F] cursor-pointer">Vendor Pricing</Link>
              <Link to="/partnership" className="block text-sm font-semibold text-[#059669] cursor-pointer">Partnership</Link>
              <Link to="/why-mso" className="block text-sm font-medium text-gray-700 hover:text-[#1B4B7F] cursor-pointer">Why MSO?</Link>
              <Link to="/audit" className="block text-sm font-semibold text-[#BE185D] cursor-pointer">Request Audit</Link>
            </div>
          </div>
        )}
      </nav>

      {/* HERO */}
      <section className="relative min-h-[72vh] flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/031f48496fd6103ea9ddd0a0ed86e4e9.jpg"
            alt="Practice Audit"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0A1628]/96 via-[#0A1628]/85 to-[#0A1628]/50"></div>
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 py-40 w-full">
          <div className="inline-flex items-center gap-2 bg-[#BE185D]/20 border border-[#BE185D]/40 text-[#F9A8D4] px-5 py-2 rounded-full text-xs font-bold tracking-widest mb-8">
            <i className="ri-file-search-line text-base"></i>
            PRACTICE AUDIT &amp; REVIEW
          </div>
          <h1 className="text-6xl lg:text-8xl font-black text-white leading-none mb-6">
            Find the Leaks<br />
            Before They<br />
            <span className="text-[#F9A8D4]">Sink You.</span>
          </h1>
          <p className="text-xl text-gray-300 leading-relaxed mb-10 max-w-2xl">
            92% of practices we audit are losing revenue, risking compliance violations, or operating with hidden inefficiencies. Our expert-led audit finds exactly where — and tells you exactly how to fix it.
          </p>
          <div className="flex flex-wrap gap-4">
            <a
              href="#request-form"
              className="bg-[#BE185D] text-white px-8 py-4 rounded-full text-sm font-bold hover:bg-[#9D174D] transition-all flex items-center gap-2 whitespace-nowrap cursor-pointer"
            >
              Request Your Free Mini Audit
              <i className="ri-arrow-down-line"></i>
            </a>
            <a
              href="#audit-tiers"
              className="bg-transparent border-2 border-white text-white px-8 py-4 rounded-full text-sm font-bold hover:bg-white hover:text-[#0A1628] transition-all whitespace-nowrap cursor-pointer"
            >
              Compare Audit Options
            </a>
          </div>
        </div>
      </section>

      {/* STATS STRIP */}
      <section className="bg-[#0A1628] py-14">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 text-center">
            {stats.map((s, i) => (
              <div key={i}>
                <div className="text-4xl font-black text-[#F9A8D4] mb-1">{s.value}</div>
                <div className="text-gray-400 text-xs leading-relaxed">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHAT WE REVIEW */}
      <section className="py-24 bg-[#FAF8F5]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-14">
            <p className="text-xs uppercase tracking-[3px] text-[#BE185D] mb-4 font-semibold">THE AUDIT SCOPE</p>
            <h2 className="text-5xl font-black text-gray-900 mb-4">Five Critical Areas. Zero Guesswork.</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Every audit covers the same five pillars — the areas where practices consistently lose the most money and face the greatest risk.
            </p>
          </div>
          <div className="grid md:grid-cols-3 lg:grid-cols-5 gap-5">
            {auditAreas.map((area, i) => (
              <div
                key={i}
                className="bg-white rounded-2xl p-8 text-center border border-gray-100 hover:-translate-y-1 transition-all duration-300"
                style={{ borderTop: `3px solid ${area.color}` }}
              >
                <div
                  className="w-14 h-14 rounded-2xl flex items-center justify-center mx-auto mb-5"
                  style={{ backgroundColor: `${area.color}15` }}
                >
                  <i className={`${area.icon} text-2xl`} style={{ color: area.color }}></i>
                </div>
                <div className="text-sm font-black text-gray-900 leading-tight">{area.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* AUDIT TIERS */}
      <section id="audit-tiers" className="py-24 bg-white">
        <div className="max-w-5xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-14">
            <p className="text-xs uppercase tracking-[3px] text-[#BE185D] mb-4 font-semibold">CHOOSE YOUR LEVEL</p>
            <h2 className="text-5xl font-black text-gray-900 mb-4">Two Audit Options</h2>
            <p className="text-lg text-gray-600 max-w-xl mx-auto">Start with the free Mini Audit or go straight to the comprehensive Full Practice Audit.</p>
          </div>
          <div className="grid md:grid-cols-2 gap-8">
            {auditTiers.map((tier) => (
              <div
                key={tier.id}
                onClick={() => setSelectedTier(tier.id as 'mini' | 'full')}
                className={`rounded-3xl border-2 p-10 cursor-pointer transition-all duration-300 relative ${
                  selectedTier === tier.id
                    ? 'border-[#BE185D] bg-[#FFF1F5]'
                    : 'border-gray-200 bg-white hover:border-gray-300'
                }`}
              >
                {tier.id === 'full' && (
                  <div className="absolute top-0 right-0 bg-[#BE185D] text-white text-xs font-black px-5 py-2 rounded-bl-2xl rounded-tr-3xl tracking-widest">
                    MOST POPULAR
                  </div>
                )}
                {selectedTier === tier.id && (
                  <div className="absolute top-4 left-4 w-6 h-6 bg-[#BE185D] rounded-full flex items-center justify-center">
                    <i className="ri-check-line text-white text-xs font-bold"></i>
                  </div>
                )}
                <div
                  className="inline-block text-xs font-black tracking-widest px-3 py-1 rounded-full mb-5 text-white"
                  style={{ backgroundColor: tier.badgeColor }}
                >
                  {tier.badge}
                </div>
                <h3 className="text-2xl font-black text-gray-900 mb-1">{tier.name}</h3>
                <div className="flex items-center gap-4 mb-4">
                  <span className="text-sm font-bold" style={{ color: tier.badgeColor }}>{tier.price}</span>
                  <span className="text-gray-400 text-xs">·</span>
                  <span className="text-gray-500 text-xs flex items-center gap-1">
                    <i className="ri-time-line"></i>
                    {tier.time}
                  </span>
                </div>
                <p className="text-gray-500 text-sm leading-relaxed mb-7">{tier.desc}</p>
                <ul className="space-y-2.5">
                  {tier.includes.map((item, ii) => (
                    <li key={ii} className="flex items-start gap-3 text-sm text-gray-700">
                      <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <i className="ri-check-line font-bold" style={{ color: tier.badgeColor }}></i>
                      </div>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
          <p className="text-center text-gray-500 text-sm mt-6">
            <i className="ri-information-line mr-1"></i>
            Click a card to pre-select it in the request form below
          </p>
        </div>
      </section>

      {/* REQUEST FORM */}
      <section id="request-form" className="py-24 bg-[#FAF8F5]">
        <div className="max-w-6xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-5 gap-16 items-start">

            {/* Left — context */}
            <div className="lg:col-span-2">
              <p className="text-xs uppercase tracking-[3px] text-[#BE185D] mb-4 font-semibold">GET STARTED</p>
              <h2 className="text-4xl font-black text-gray-900 leading-tight mb-6">
                Request Your<br />Practice Audit
              </h2>
              <p className="text-gray-600 leading-relaxed mb-8">
                Fill out the form and our team will reach out within one business day to confirm your audit type, sign an NDA, and schedule your kickoff.
              </p>

              <div className="space-y-5">
                {[
                  { icon: 'ri-lock-line', title: 'Confidential & Secure', desc: 'NDA signed before any data is shared. Your information never leaves our team.' },
                  { icon: 'ri-time-line', title: 'Response Within 24 Hours', desc: 'A dedicated audit specialist will contact you the next business day.' },
                  { icon: 'ri-file-text-line', title: 'No Obligation', desc: 'Receiving the audit report does not commit you to any ongoing engagement.' },
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-[#BE185D]/10 rounded-xl flex items-center justify-center flex-shrink-0">
                      <i className={`${item.icon} text-[#BE185D] text-lg`}></i>
                    </div>
                    <div>
                      <div className="text-sm font-black text-gray-900 mb-0.5">{item.title}</div>
                      <div className="text-xs text-gray-500 leading-relaxed">{item.desc}</div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-10 bg-white rounded-2xl p-6 border border-gray-100">
                <div className="text-xs font-bold text-gray-500 uppercase tracking-widest mb-3">What to expect</div>
                <ol className="space-y-3">
                  {[
                    'Submit this form',
                    'We contact you within 24 hours',
                    'NDA signed & kickoff scheduled',
                    'Audit conducted by our specialists',
                    'Report delivered with debrief call',
                  ].map((step, i) => (
                    <li key={i} className="flex items-center gap-3 text-sm text-gray-700">
                      <span className="w-6 h-6 bg-[#BE185D] text-white rounded-full text-xs font-black flex items-center justify-center flex-shrink-0">
                        {i + 1}
                      </span>
                      {step}
                    </li>
                  ))}
                </ol>
              </div>
            </div>

            {/* Right — form */}
            <div className="lg:col-span-3">
              {formStatus === 'success' ? (
                <div className="bg-white rounded-3xl p-14 text-center border border-gray-100">
                  <div className="w-20 h-20 bg-[#BE185D]/10 rounded-full flex items-center justify-center mx-auto mb-6">
                    <i className="ri-check-double-line text-[#BE185D] text-4xl"></i>
                  </div>
                  <h3 className="text-3xl font-black text-gray-900 mb-3">Request Received!</h3>
                  <p className="text-gray-600 leading-relaxed mb-8 max-w-sm mx-auto">
                    Thank you! A MedxMSO audit specialist will reach out within one business day to confirm your audit type and schedule your kickoff.
                  </p>
                  <button
                    onClick={() => setFormStatus('idle')}
                    className="bg-[#BE185D] text-white px-8 py-3.5 rounded-full text-sm font-bold hover:bg-[#9D174D] transition-all cursor-pointer whitespace-nowrap"
                  >
                    Submit Another Request
                  </button>
                </div>
              ) : (
                <form
                  ref={formRef}
                  id="audit-request-form"
                  data-readdy-form
                  onSubmit={handleSubmit}
                  className="bg-white rounded-3xl p-10 border border-gray-100"
                >
                  <h3 className="text-xl font-black text-gray-900 mb-8">Practice &amp; Contact Information</h3>

                  {/* Name row */}
                  <div className="grid sm:grid-cols-2 gap-5 mb-5">
                    <div>
                      <label className="block text-xs font-bold text-gray-700 mb-2 uppercase tracking-wide">First Name *</label>
                      <input
                        type="text"
                        name="first_name"
                        required
                        placeholder="Jane"
                        className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:border-[#BE185D] transition-colors"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-gray-700 mb-2 uppercase tracking-wide">Last Name *</label>
                      <input
                        type="text"
                        name="last_name"
                        required
                        placeholder="Smith"
                        className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:border-[#BE185D] transition-colors"
                      />
                    </div>
                  </div>

                  {/* Title & Practice */}
                  <div className="grid sm:grid-cols-2 gap-5 mb-5">
                    <div>
                      <label className="block text-xs font-bold text-gray-700 mb-2 uppercase tracking-wide">Your Title / Role *</label>
                      <select
                        name="title"
                        required
                        className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-900 focus:outline-none focus:border-[#BE185D] transition-colors bg-white cursor-pointer"
                      >
                        <option value="">Select your role</option>
                        <option value="Physician / MD / DO">Physician / MD / DO</option>
                        <option value="Practice Owner">Practice Owner</option>
                        <option value="Practice Administrator">Practice Administrator</option>
                        <option value="Office Manager">Office Manager</option>
                        <option value="CFO / Controller">CFO / Controller</option>
                        <option value="Billing Manager">Billing Manager</option>
                        <option value="Other">Other</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-gray-700 mb-2 uppercase tracking-wide">Practice Name *</label>
                      <input
                        type="text"
                        name="practice_name"
                        required
                        placeholder="Sunrise Family Medicine"
                        className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:border-[#BE185D] transition-colors"
                      />
                    </div>
                  </div>

                  {/* Email & Phone */}
                  <div className="grid sm:grid-cols-2 gap-5 mb-5">
                    <div>
                      <label className="block text-xs font-bold text-gray-700 mb-2 uppercase tracking-wide">Email Address *</label>
                      <input
                        type="email"
                        name="email"
                        required
                        placeholder="jane@practice.com"
                        className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:border-[#BE185D] transition-colors"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-gray-700 mb-2 uppercase tracking-wide">Phone Number *</label>
                      <input
                        type="tel"
                        name="phone"
                        required
                        placeholder="(555) 000-0000"
                        className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:border-[#BE185D] transition-colors"
                      />
                    </div>
                  </div>

                  {/* Specialty & Providers */}
                  <div className="grid sm:grid-cols-2 gap-5 mb-5">
                    <div>
                      <label className="block text-xs font-bold text-gray-700 mb-2 uppercase tracking-wide">Practice Specialty *</label>
                      <select
                        name="specialty"
                        required
                        className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-900 focus:outline-none focus:border-[#BE185D] transition-colors bg-white cursor-pointer"
                      >
                        <option value="">Select specialty</option>
                        <option value="Primary Care / Family Medicine">Primary Care / Family Medicine</option>
                        <option value="Internal Medicine">Internal Medicine</option>
                        <option value="Cardiology">Cardiology</option>
                        <option value="Orthopedics">Orthopedics</option>
                        <option value="Dermatology">Dermatology</option>
                        <option value="Pediatrics">Pediatrics</option>
                        <option value="OB/GYN">OB/GYN</option>
                        <option value="Psychiatry / Behavioral Health">Psychiatry / Behavioral Health</option>
                        <option value="Neurology">Neurology</option>
                        <option value="Oncology">Oncology</option>
                        <option value="Pharmacy">Pharmacy</option>
                        <option value="Laboratory">Laboratory</option>
                        <option value="Multi-Specialty">Multi-Specialty</option>
                        <option value="Other">Other</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-gray-700 mb-2 uppercase tracking-wide">Number of Providers</label>
                      <select
                        name="provider_count"
                        className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-900 focus:outline-none focus:border-[#BE185D] transition-colors bg-white cursor-pointer"
                      >
                        <option value="">Select range</option>
                        <option value="1 (Solo)">1 (Solo)</option>
                        <option value="2–5">2–5</option>
                        <option value="6–10">6–10</option>
                        <option value="11–25">11–25</option>
                        <option value="26+">26+</option>
                      </select>
                    </div>
                  </div>

                  {/* Audit Type */}
                  <div className="mb-5">
                    <label className="block text-xs font-bold text-gray-700 mb-3 uppercase tracking-wide">Audit Type Requested *</label>
                    <div className="grid sm:grid-cols-2 gap-4">
                      {auditTiers.map((tier) => (
                        <label
                          key={tier.id}
                          className={`flex items-start gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all ${
                            selectedTier === tier.id ? 'border-[#BE185D] bg-[#FFF1F5]' : 'border-gray-200 hover:border-gray-300'
                          }`}
                          onClick={() => setSelectedTier(tier.id as 'mini' | 'full')}
                        >
                          <input
                            type="radio"
                            name="audit_type"
                            value={tier.name}
                            checked={selectedTier === tier.id}
                            onChange={() => setSelectedTier(tier.id as 'mini' | 'full')}
                            className="mt-0.5 accent-[#BE185D] cursor-pointer"
                          />
                          <div>
                            <div className="text-sm font-black text-gray-900">{tier.name}</div>
                            <div className="text-xs text-gray-500 mt-0.5">{tier.price} · {tier.time}</div>
                          </div>
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* Areas of concern */}
                  <div className="mb-5">
                    <label className="block text-xs font-bold text-gray-700 mb-3 uppercase tracking-wide">Primary Areas of Concern (select all that apply)</label>
                    <div className="grid sm:grid-cols-2 gap-3">
                      {auditAreas.map((area, i) => (
                        <label key={i} className="flex items-center gap-3 cursor-pointer group">
                          <input
                            type="checkbox"
                            name="concern_areas"
                            value={area.label}
                            className="w-4 h-4 rounded accent-[#BE185D] cursor-pointer"
                          />
                          <span className="text-sm text-gray-700 group-hover:text-gray-900 transition-colors">{area.label}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* Additional notes */}
                  <div className="mb-8">
                    <label className="block text-xs font-bold text-gray-700 mb-2 uppercase tracking-wide">
                      Additional Context
                      <span className="ml-2 text-gray-400 normal-case font-normal">({charCount}/500)</span>
                    </label>
                    <textarea
                      name="additional_context"
                      rows={4}
                      maxLength={500}
                      placeholder="Briefly describe any specific concerns, recent issues, or goals you have for this audit..."
                      onChange={(e) => setCharCount(e.target.value.length)}
                      className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:border-[#BE185D] transition-colors resize-none"
                    />
                    {charCount >= 500 && (
                      <p className="text-xs text-[#BE185D] mt-1">Character limit reached (500 max)</p>
                    )}
                  </div>

                  {formStatus === 'error' && (
                    <div className="mb-5 bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-3 rounded-xl">
                      Something went wrong. Please try again or contact us directly.
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={formStatus === 'submitting'}
                    className="w-full bg-[#BE185D] text-white py-4 rounded-full text-sm font-bold hover:bg-[#9D174D] transition-all flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {formStatus === 'submitting' ? (
                      <>
                        <i className="ri-loader-4-line animate-spin text-lg"></i>
                        Submitting...
                      </>
                    ) : (
                      <>
                        Submit Audit Request
                        <i className="ri-arrow-right-line text-lg"></i>
                      </>
                    )}
                  </button>
                  <p className="text-center text-xs text-gray-400 mt-4">
                    By submitting, you agree to be contacted by a MedxMSO specialist. No spam, ever.
                  </p>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-24 bg-white">
        <div className="max-w-3xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-14">
            <p className="text-xs uppercase tracking-[3px] text-[#BE185D] mb-4 font-semibold">COMMON QUESTIONS</p>
            <h2 className="text-4xl font-black text-gray-900">Audit FAQs</h2>
          </div>
          <div className="space-y-3">
            {faqs.map((faq, i) => (
              <div
                key={i}
                className="border border-gray-200 rounded-2xl overflow-hidden"
              >
                <button
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  className="w-full flex items-center justify-between px-7 py-5 text-left cursor-pointer hover:bg-gray-50 transition-colors"
                >
                  <span className="text-sm font-black text-gray-900 pr-4">{faq.q}</span>
                  <i className={`ri-${openFaq === i ? 'subtract' : 'add'}-line text-[#BE185D] text-xl flex-shrink-0`}></i>
                </button>
                {openFaq === i && (
                  <div className="px-7 pb-6 text-sm text-gray-600 leading-relaxed border-t border-gray-100 pt-4">
                    {faq.a}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* BOTTOM CTA */}
      <section className="relative py-28 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=Medical%20practice%20team%20reviewing%20audit%20results%20together%20in%20bright%20modern%20conference%20room%20with%20documents%20charts%20and%20laptops%20on%20table%20diverse%20healthcare%20professionals%20and%20administrators%20in%20professional%20business%20attire%20discussing%20findings%20in%20clean%20contemporary%20office%20with%20large%20windows%20and%20neutral%20warm%20tones&width=1920&height=800&seq=audit-cta-bottom&orientation=landscape"
            alt="Audit Results Review"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0A1628]/96 via-[#0A1628]/85 to-transparent"></div>
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8">
          <div className="max-w-2xl">
            <p className="text-xs uppercase tracking-[3px] text-[#F9A8D4] mb-6 font-semibold">NOT READY FOR A FULL AUDIT?</p>
            <h2 className="text-5xl lg:text-6xl font-black text-white leading-tight mb-6">
              Start with the<br />
              <span className="text-[#F9A8D4]">Free Mini Audit.</span>
            </h2>
            <p className="text-lg text-gray-300 leading-relaxed mb-10">
              No commitment. No credit card. Just a fast, expert look at your practice across 5 critical areas — and a clear summary of what needs attention most.
            </p>
            <div className="flex flex-wrap gap-4">
              <a
                href="#request-form"
                className="bg-[#BE185D] text-white px-10 py-5 rounded-full text-base font-bold hover:bg-[#9D174D] transition-all flex items-center gap-3 whitespace-nowrap cursor-pointer"
              >
                Request Free Mini Audit
                <i className="ri-arrow-right-line text-xl"></i>
              </a>
              <Link
                to="/services#audit-review"
                className="bg-transparent border-2 border-white text-white px-10 py-5 rounded-full text-base font-bold hover:bg-white hover:text-[#0A1628] transition-all whitespace-nowrap cursor-pointer"
              >
                Learn About Our Services
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#0A1628] relative overflow-hidden">
        <div className="absolute bottom-0 left-0 right-0 text-[#1A2942] text-[180px] font-bold opacity-50 leading-none select-none pointer-events-none">
          MEDXMSO
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 py-16">
          <div className="grid md:grid-cols-4 gap-12">
            <div>
              <div className="text-white text-4xl font-bold mb-3">MedxMSO</div>
              <p className="text-gray-400 text-sm mb-16">Healthcare Management Excellence</p>
              <div className="text-white text-lg leading-relaxed">
                <p>Empowering providers</p>
                <p className="text-gray-400">since 2015</p>
              </div>
            </div>
            <div>
              <h4 className="text-white text-lg font-bold mb-6">Audit Services</h4>
              <ul className="space-y-4">
                <li><a href="#audit-tiers" className="text-gray-400 text-sm hover:text-white transition-colors cursor-pointer">Mini Practice Audit</a></li>
                <li><a href="#audit-tiers" className="text-gray-400 text-sm hover:text-white transition-colors cursor-pointer">Full Practice Audit</a></li>
                <li><a href="#request-form" className="text-[#F9A8D4] text-sm hover:text-white transition-colors cursor-pointer">Request an Audit</a></li>
              </ul>
            </div>
            <div>
              <h4 className="text-white text-lg font-bold mb-6">Pages</h4>
              <ul className="space-y-4">
                <li><Link to="/" className="text-gray-400 text-sm hover:text-white transition-colors cursor-pointer">Home</Link></li>
                <li><Link to="/services" className="text-gray-400 text-sm hover:text-white transition-colors cursor-pointer">Services</Link></li>
                <li><Link to="/pricing" className="text-gray-400 text-sm hover:text-white transition-colors cursor-pointer">Models</Link></li>
                <li><Link to="/mso-pricing" className="text-gray-400 text-sm hover:text-white transition-colors cursor-pointer">Vendor Pricing</Link></li>
                <li><Link to="/partnership" className="text-[#6EE7B7] text-sm hover:text-white transition-colors cursor-pointer">Partnership</Link></li>
                <li><Link to="/why-mso" className="text-gray-400 text-sm hover:text-white transition-colors cursor-pointer">Why MSO?</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="text-white text-lg font-bold mb-6">Connect</h4>
              <ul className="space-y-4">
                <li><a href="#" className="flex items-center gap-3 text-gray-400 text-sm hover:text-white transition-colors cursor-pointer"><i className="ri-linkedin-line text-xl"></i>LinkedIn</a></li>
                <li><a href="#" className="flex items-center gap-3 text-gray-400 text-sm hover:text-white transition-colors cursor-pointer"><i className="ri-twitter-x-line text-xl"></i>Twitter</a></li>
                <li><a href="#" className="flex items-center gap-3 text-gray-400 text-sm hover:text-white transition-colors cursor-pointer"><i className="ri-facebook-line text-xl"></i>Facebook</a></li>
              </ul>
            </div>
          </div>
          <div className="mt-16 pt-8 border-t border-gray-800 flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-gray-500 text-sm">© 2024 MedxMSO. All rights reserved.</p>
            <div className="flex gap-6">
              <a href="#" className="text-gray-500 text-sm hover:text-white transition-colors cursor-pointer">Privacy Policy</a>
              <a href="#" className="text-gray-500 text-sm hover:text-white transition-colors cursor-pointer">Terms of Service</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
